<template>
  <MonitorCard :title="'Agent Status'">
    <template #filter>
      <div class="agent-filter">
        <el-button
          v-for="{ value, label } in Object.values(agentFilter)"
          :class="{
            [value]: true,
            'is-active': selectedAgentStatus === value,
          }"
          @click="selectedAgentStatus = value"
          size="mini"
          :key="`filter-${value}`"
          >{{ label }}</el-button
        >
      </div>
    </template>
    <template #content>
      <el-card
        v-for="agent in filteredAgents"
        :key="agent.id"
        class="agent-monitor"
        shadow="never"
      >
        <div class="agent-info">
          <div
            :class="{
              indicator: true,
              [getAgentStatus(agent).toLowerCase()]: true,
            }"
          ></div>
          <div class="container">
            <p class="name">{{ agent.name }}</p>
            <p class="status">
              {{ getAgentStatus(agent) }}
              <span
                v-if="
                  departments.length > 0 && parseAgentDepartments(agent)
                "
                class="department"
              >
                · {{ parseAgentDepartments(agent).join(', ') }}
              </span>
            </p>
          </div>
        </div>
        <div
          v-if="agent.status !== 'OFFLINE' && agent.attending"
          :class="{
            'chat-counts': true,
            'chat-counts-available':
              agent.attending < getAgentChatLimit(agent.email),
            'chat-counts-engaged':
              agent.attending >= getAgentChatLimit(agent.email),
          }"
        >
          <span v-if="getAgentChatLimit(agent.email)">
            {{ agent.attending }}/{{ getAgentChatLimit(agent.email) }}
            chats
          </span>
          <span v-else> {{ agent.attending }} chats </span>
        </div>
      </el-card>
    </template>
  </MonitorCard>
</template>
<script>
import { mapGetters } from 'vuex';
import MonitorCard from './MonitorCard.vue';
import { liveAnalyticsMixin } from '@/mixins/liveAnalytics';
import _ from 'lodash';

const STATUS_ORDER = { engaged: 0, available: 1, offline: 2 };

export default {
  mixins: [liveAnalyticsMixin],
  components: {
    MonitorCard,
  },
  props: {
    selectedDepartments: {
      type: Array,
      default: () => [],
    },
    activeTab: {
      type: String,
      default: '',
    },
  },
  data() {
    const agentFilter = {
      ALL: {
        label: 'All',
        value: 'all',
      },
      ENGAGED: {
        label: 'Engaged',
        value: 'engaged',
      },
      AVAILABLE: {
        label: 'Available',
        value: 'available',
      },
      OFFLINE: {
        label: 'Offline',
        value: 'offline',
      },
    };

    return {
      agentFilter,
      selectedAgentStatus: agentFilter.AVAILABLE.value,
    };
  },
  computed: {
    ...mapGetters([
      'allAgents',
      'onlineAgents',
      'offlineAgents',
      'engagedAgents',
      'availableAgents',
    ]),
    deptIds() {
      return this.selectedDepartments.length > 0 ? this.selectedDepartments.flat() : this.departments.flatMap(dept => dept.value);
    },
    deptNames() {
      return this.convertDepartmentIdsToNames(this.deptIds).map((v) => v.toLowerCase());
    },
    handoverOptions() {
      return this.$store.state.modules?.handover || {};
    },
    defaultChatLimit() {
      return this.handoverOptions.chat_limit || 0;
    },
    chatLimitPerAgent() {
      return this.handoverOptions.chatLimitPerAgent || [];
    },
    filteredAgents() {
      const filter = this.agentFilter;
      let agents;

      switch (this.selectedAgentStatus) {
        case filter.OFFLINE.value:
          agents = this.offlineAgents;
          break;

        case filter.ENGAGED.value:
          agents = this.engagedAgents;
          break;

        case filter.AVAILABLE.value:
          agents = this.availableAgents;
          break;

        case filter.ALL.value:
        default:
          agents = this.allAgents;
          break;
      }

      // Apply department filter
      agents = agents.filter((agent) =>
        this.agentMatchesDepartmentFilter(agent, this.deptNames),
      );

      // Sort by status: Engaged -> Available -> Offline
      return [...agents].sort((a, b) => {
        const aStatus = this.getAgentStatusKey(a);
        const bStatus = this.getAgentStatusKey(b);
        return STATUS_ORDER[aStatus] - STATUS_ORDER[bStatus];
      });
    },
  },
  methods: {
    getAgentChatLimit(email) {
      if (!email) return this.defaultChatLimit;

      const setting = this.chatLimitPerAgent.find(
        (setting) => setting.agentId?.toLowerCase() === email.toLowerCase(),
      );

      return setting?.limit ?? this.defaultChatLimit;
    },
    getAgentStatus(agent) {
      if (agent.status === 'OFFLINE') {
        return 'Offline';
      }

      const chatLimit = this.getAgentChatLimit(agent.email);

      if (!chatLimit || chatLimit === 0) return 'Available';

      if (agent.attending >= chatLimit) return 'Engaged';

      return 'Available';
    },
    // Helper for sorting - returns lowercase status key
    getAgentStatusKey(agent) {
      return this.getAgentStatus(agent).toLowerCase();
    },
  },
  watch: {
    activeTab(newVal) {
      if (newVal !== 'live-analytics') {
        this.selectedAgentStatus = this.agentFilter.AVAILABLE.value;
      }
    },
  },
  mounted() {
    // Fetch all agents with chat count from DB
    this.$store.dispatch('supervisor/FETCH_AGENTS_WITH_CHAT_COUNT', {
      email: this.$store.state.profile.email,
    });
  },
};
</script>
<style lang="scss" scoped>
@import '../../../assets/scss/colors.scss';

$color-all: rgba(0, 0, 0, 0.8);
$color-engaged: #{$color-primary};
$color-available: #{$color-success};
$color-offline: #{$color-grey};

.agent-filter {
  display: flex;
  gap: 0.25rem;

  ::v-deep .el-button {
    margin: 0;
    border-radius: 0.75rem;

    &.all {
      &.is-active,
      &:hover,
      &:focus {
        border-color: $color-all;
        background-color: $color-all;
        color: white;
      }
    }

    &.engaged {
      &.is-active,
      &:hover,
      &:focus {
        border-color: $color-engaged;
        background-color: $color-engaged;
        color: white;
      }
    }

    &.available {
      &.is-active,
      &:hover,
      &:focus {
        border-color: $color-available;
        background-color: $color-available;
        color: white;
      }
    }

    &.offline {
      &.is-active,
      &:hover,
      &:focus {
        border-color: $color-offline;
        background-color: $color-offline;
        color: white;
      }
    }
  }
}

.agent-monitor {
  border-radius: 0.5rem;
  box-shadow: none;

  &:hover {
    box-shadow: 0 0.25rem 0.5rem 0 rgba(#000, 0.1);
  }

  ::v-deep .el-card__body {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    box-shadow: none;
  }
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .indicator {
    width: 0.65rem;
    height: 0.65rem;
    border-radius: 50%;

    &.engaged {
      background-color: $color-engaged;
    }

    &.available {
      background-color: $color-available;
    }

    &.offline {
      background-color: $color-offline;
    }
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    p {
      margin: 0;
    }

    .name {
      font-size: 1rem;
      font-weight: 700;
    }

    .status {
      font-size: 0.7rem;
      color: $color-grey;
      font-weight: 600;
    }
  }
}

.chat-counts {
  font-size: 0.7rem;
  padding: 0.3rem 0.4rem;
  border-radius: 0.3rem;
  font-weight: 700;

  &-available {
    background-color: rgba($color-warning, 0.15);
    color: $color-warning;
  }

  &-engaged {
    background-color: rgba($color-danger, 0.15);
    color: $color-danger;
  }
}
</style>
